import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { apiClient } from '@dukene/shared';
import { Mail, ArrowRight, AlertCircle, CheckCircle2 } from 'lucide-react';

const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPassword() {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      setError('');
      setSuccess('');
      setIsLoading(true);
      const response = await apiClient.post<{ detail: string }>('/auth/forgot-password', data);
      setSuccess(response.detail || 'If an account exists for that email, a reset link has been sent.');
    } catch (err: any) {
      setError(err.message || 'Unable to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white">
      <div className="hidden lg:flex lg:w-1/2 bg-white border-r border-gray-100 relative">
        <div className="flex flex-col justify-between w-full p-16 xl:p-24">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center">
              <img src="/favicon.png" alt="Logo" className="w-full h-full object-contain p-0.5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-black tracking-tight">Dukene</h1>
              <p className="text-xs text-gray-500 font-medium tracking-wide">MERCHANTS</p>
            </div>
          </div>

          <div className="space-y-16">
            <div>
              <h2 className="text-6xl font-light text-black mb-6 leading-tight tracking-tight">
                Recover
                <br />
                Access
              </h2>
              <div className="w-16 h-0.5 bg-black mb-6" />
              <p className="text-lg text-gray-600 leading-relaxed font-light">
                We&apos;ll send a secure email link so you can reset your password and get back into your merchant account.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-8 text-xs text-gray-400 font-light">
            <span>© 2024 Dukene</span>
            <span className="w-1 h-1 rounded-full bg-gray-300" />
            <span>All rights reserved</span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center bg-white px-8 lg:px-20 xl:px-32 py-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center justify-center gap-3 mb-16">
            <div className="w-10 h-10 bg-white border border-gray-200 rounded-lg flex items-center justify-center">
              <img src="/favicon.png" alt="Logo" className="w-full h-full object-contain p-0.5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-black">Dukene</h1>
              <p className="text-xs text-gray-500 font-medium">MERCHANTS</p>
            </div>
          </div>

          <div className="mb-12">
            <h2 className="text-4xl font-light text-black mb-3 tracking-tight">Forgot password?</h2>
            <p className="text-gray-500 text-base font-light">
              Enter your email and we&apos;ll send a reset link.
            </p>
          </div>

          {error && (
            <div className="mb-8 border border-red-200 rounded-lg p-4 bg-red-50">
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-red-900 text-sm mb-1">Error</p>
                  <p className="text-sm text-red-700 font-light">{error}</p>
                </div>
              </div>
            </div>
          )}

          {success ? (
            <div className="border border-emerald-200 rounded-xl p-6 bg-emerald-50">
              <div className="flex items-start gap-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-3">
                  <div>
                    <h3 className="text-lg font-medium text-emerald-950 mb-1">Check your inbox</h3>
                    <p className="text-sm text-emerald-800 font-light leading-relaxed">{success}</p>
                  </div>
                  <p className="text-xs text-emerald-700 font-light leading-relaxed">
                    If you do not see the email in a few minutes, check your spam folder or request another link.
                  </p>
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-2 text-sm font-medium text-emerald-900 hover:text-black transition-colors"
                  >
                    Back to login
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-black mb-3">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    {...register('email')}
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    className="h-12 w-full rounded-lg border border-gray-200 bg-white text-black placeholder:text-gray-400 focus:border-black focus:outline-none focus:ring-1 focus:ring-black pl-12 pr-4 text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed font-light"
                    disabled={isLoading}
                  />
                </div>
                {errors.email && <p className="text-sm text-red-600 mt-2 font-light">{errors.email.message}</p>}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 bg-black hover:bg-gray-900 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Sending link...</span>
                  </>
                ) : (
                  <>
                    Send reset link
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          )}

          <div className="mt-12 text-center">
            <Link to="/login" className="text-sm text-gray-600 hover:text-black font-light transition-colors">
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
